import React from 'react';
import { Box, Container, Typography } from '@mui/material';

const steps = [
  { num: '01', title: 'We start with your process', body: "Before recommending anything, we understand how your team actually works today — the spreadsheets, the WhatsApp groups, the paper forms." },
  { num: '02', title: 'We build or configure the right system', body: "Whether that's one of our SaaS products or a custom setup using third-party tools, we implement it properly — not just hand you a login." },
  { num: '03', title: 'We stay with you', body: "Onboarding, training, ongoing support. We're not a one-time vendor. We're the IT partner your business can actually rely on." },
];

const About = () => (
  <Box id="about" sx={{ bgcolor: '#FFFFFF' }}>
    {/* Statement banner */}
    <Box sx={{ py: { xs: 7, md: 12 }, bgcolor: '#F8F9FF', borderTop: '1px solid #F3F4F6', borderBottom: '1px solid #F3F4F6', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 800, height: 400, background: 'radial-gradient(ellipse, rgba(99,102,241,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 3 }}>About InfyniHub</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2.2rem', md: '3rem' }, lineHeight: 1.15, letterSpacing: '-0.02em', color: '#0F1117', mb: 3 }}>
          Most SMEs are running critical operations on tools{' '}
          <Box component="span" sx={{ color: '#6366F1' }}>that were never designed for them.</Box>
        </Typography>
        <Typography sx={{ color: '#6B7280', fontSize: { xs: '1rem', md: '1.05rem' }, lineHeight: 1.85, maxWidth: 620, mx: 'auto' }}>
          Most SMEs we spoke to were running real businesses — growing teams, real customers, real complexity — but still managing everything through WhatsApp threads, paper forms, and spreadsheets held together with hope. Not because they didn't want better tools. Because every "solution" they found was built for companies ten times their size, priced accordingly, and impossible to implement without a dedicated IT team.
        </Typography>
        <Typography sx={{ color: '#6B7280', fontSize: { xs: '1rem', md: '1.05rem' }, lineHeight: 1.85, maxWidth: 620, mx: 'auto', mt: 2 }}>
          That's why we built InfyniHub. Not as another software vendor, but as the partner we wished these businesses already had.
        </Typography>
      </Container>
    </Box>

    {/* How we work */}
    <Box sx={{ py: { xs: 7, md: 12 }, bgcolor: '#FFFFFF' }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 6, md: 12 }, alignItems: 'start' }}>
          {/* Left */}
          <Box>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>How We Work</Typography>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.8rem', md: '2.6rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', mb: 3 }}>
              Not a vendor.<br />A partner.
            </Typography>
            <Typography sx={{ color: '#6B7280', lineHeight: 1.85, fontSize: '0.95rem', mb: 5 }}>
              We don't sell you software and disappear. We work with you to understand your operations, implement the right solution, and make sure your team actually uses it.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {[
                { v: 'SME', l: 'Focused exclusively' },
                { v: '5+', l: 'IT service categories' },
                { v: '2', l: 'Proprietary SaaS products' },
                { v: 'E2E', l: 'End-to-end delivery' },
              ].map(s => (
                <Box key={s.l} sx={{ bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', borderRadius: '14px', p: 2.5 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: '1.8rem', color: '#0F1117', letterSpacing: '-0.02em', lineHeight: 1 }}>{s.v}</Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.78rem', mt: 0.5 }}>{s.l}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right — timeline */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {steps.map((s, i) => (
              <Box key={s.num} sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#6366F1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', color: '#fff' }}>{s.num}</Typography>
                  </Box>
                  {i < steps.length - 1 && <Box sx={{ width: 1, flex: 1, minHeight: 32, bgcolor: 'rgba(99,102,241,0.2)', my: 0.5 }} />}
                </Box>
                <Box sx={{ pb: i < steps.length - 1 ? 5 : 0, pt: 0.5 }}>
                  <Typography sx={{ fontWeight: 800, color: '#0F1117', fontSize: '1rem', mb: 1 }}>{s.title}</Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.88rem', lineHeight: 1.75 }}>{s.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  </Box>
);

export default About;
